import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getTeamsTournamentRounds } from '../../services/tournament.service';

const headerSx = {
  background: '#565656',
  color: '#e2e8f0',
  fontWeight: 700,
  letterSpacing: 0.5,
};

const cellSx = {
  background: '#9295a9',
  color: '#1a1f2e',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0,0,0,0.15)',
};

const resultCellSx = {
  ...cellSx,
  textAlign: 'center',
  fontWeight: 700,
  fontFamily: 'monospace',
  fontSize: '0.95rem',
  width: 80,
};

const TournamentTeamsRounds = ({ torId }) => {
  const [rounds, setRounds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchRounds = async () => {
      setIsLoading(true);
      try {
        const data = await getTeamsTournamentRounds(torId);
        
        // Ordenar los enfrentamientos de cada ronda para que los BYE queden al final
        const sortedData = data.map((round) => ({
          ...round,
          partidas: [...round.partidas].sort((a, b) => {
            const isByeA = a.equ_2_id === 0;
            const isByeB = b.equ_2_id === 0;
            if (isByeA && !isByeB) return 1;
            if (!isByeA && isByeB) return -1;
            return 0;
          }),
        }));
        
        setRounds(sortedData);
      } catch (e) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRounds();
  }, [torId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#7c8cf8' }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error" sx={{ py: 3 }}>
        Error al cargar las rondas por equipo.
      </Typography>
    );
  }

  if (!rounds.length) {
    return (
      <Typography sx={{ color: '#9aa5b4', py: 3 }}>
        No hay rondas registradas para este torneo por equipos.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {rounds.map((round) => (
        <Box key={round.numero}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, maxWidth: 700, mx: 'auto' }}>
            <Typography
              variant="h6"
              sx={{ color: '#e2e8f0', fontWeight: 700, letterSpacing: 0.5 }}
            >
              Ronda {round.numero}
            </Typography>
            <Divider
              sx={{ flex: 1, borderColor: 'rgba(124,140,248,0.25)' }}
            />
            <Typography variant="caption" sx={{ color: '#9aa5b4' }}>
              {(() => {
                const n = round.partidas.length;
                return `${n} enfrentamiento${n !== 1 ? 's' : ''}`;
              })()}
            </Typography>
          </Box>

          {round.partidas.length === 0 ? (
            <Typography sx={{ color: '#9aa5b4', fontSize: '0.85rem', pl: 1 }}>
              Sin enfrentamientos registrados
            </Typography>
          ) : (
            <Box sx={{ maxWidth: 700, mx: 'auto' }}>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 0,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'transparent',
                }}
              >
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerSx}>Equipo 1</TableCell>
                      <TableCell colSpan={2} sx={{ ...headerSx, textAlign: 'center', width: 160 }}>
                        Resultado
                      </TableCell>
                      <TableCell sx={headerSx}>Equipo 2</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {round.partidas.map((p, idx) => {
                      const isBye = p.equ_2_id === 0;

                      if (isBye) {
                        return (
                          <TableRow key={idx} hover>
                            <TableCell sx={cellSx}>{p.equipo_1 ?? '—'}</TableCell>
                            <TableCell
                              colSpan={2}
                              sx={{ ...resultCellSx, letterSpacing: 2 }}
                            >
                              {p.puntos_equ_1 ?? '—'}
                            </TableCell>
                            <TableCell sx={{ ...cellSx, fontStyle: 'italic', color: '#334155' }}>
                              BYE
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return (
                        <TableRow key={idx} hover>
                          <TableCell sx={cellSx}>{p.equipo_1 ?? '—'}</TableCell>
                          <TableCell sx={resultCellSx}>{p.puntos_equ_1 ?? '—'}</TableCell>
                          <TableCell sx={resultCellSx}>{p.puntos_equ_2 ?? '—'}</TableCell>
                          <TableCell sx={cellSx}>{p.equipo_2 ?? '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default TournamentTeamsRounds;
